import { NgFor } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
} from '@angular/core';

import { PointcloudService } from '../../services/pointcloud.service';
import { ButtonComponent } from '../button/button.component';
import { CheckboxComponent } from '../checkbox/checkbox.component';

export interface ClassificationItem {
  code: number;
  name: string;
  color: [number, number, number, number];
  visible: boolean;
}

@Component({
  selector: 'app-classification-panel',
  standalone: true,
  imports: [NgFor, CheckboxComponent, ButtonComponent],
  // Inlined to avoid runtime fetch when resource inlining plugin misses new files
  template: `
    <div class="classification-panel">
      <div class="header-row">
        <span class="title">Classifications</span>
        <app-button variant="secondary" (click)="toggleAll()">
          {{ allVisible ? 'Hide All' : 'Show All' }}
        </app-button>
      </div>
      <ul>
        <li *ngFor="let c of items; trackBy: trackByCode">
          <app-checkbox
            [checked]="c.visible"
            (checkedChange)="toggleVisibility(c, $event)"
            [inline]="true"
          ></app-checkbox>
          <span class="name">{{ c.name || 'Class ' + c.code }}</span>
          <input
            type="color"
            [value]="colorToHex(c)"
            (input)="onColorChange(c, $event)"
            aria-label="Color for {{ c.name }}"
          />
        </li>
      </ul>
    </div>
  `,
  styles: [
    `
      .classification-panel {
        font-size: 13px;
      }
    `,
    `
      .classification-panel .header-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 6px;
      }
    `,
    `
      .classification-panel ul {
        list-style: none;
        margin: 0;
        padding: 0;
        display: flex;
        flex-direction: column;
        gap: 4px;
        max-height: 240px;
        overflow: auto;
      }
    `,
    `
      .classification-panel li {
        display: flex;
        align-items: center;
        gap: 8px;
        background: #fff;
        border: 1px solid #e2e6ef;
        padding: 4px 6px;
        border-radius: 4px;
      }
    `,
    `
      .classification-panel li:hover {
        border-color: #313193;
      }
    `,
    `
      .classification-panel input[type='color'] {
        width: 32px;
        height: 24px;
        padding: 0;
        border: none;
        background: transparent;
        cursor: pointer;
      }
    `,
    `
      .classification-panel .name {
        flex: 1;
      }
    `,
    `
      .classification-panel .title {
        font-weight: 600;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClassificationPanelComponent {
  @Input() items: ClassificationItem[] = [];

  private pointcloud = inject(PointcloudService);

  trackByCode = (_: number, item: ClassificationItem): number => item.code;

  get allVisible(): boolean {
    return this.items.length > 0 && this.items.every(i => i.visible);
  }

  toggleVisibility(item: ClassificationItem, event: boolean): void {
    this.pointcloud.setClassificationVisibility(item.code, event);
    item.visible = event;
  }

  toggleAll(): void {
    const allVisible = this.items.every(i => i.visible);
    this.pointcloud.toggleAllClassifications(!allVisible);
    this.items.forEach(i => (i.visible = !allVisible));
  }

  onColorChange(item: ClassificationItem, event: Event): void {
    const input = event.target as HTMLInputElement;
    const rgb = this.hexToRgb(input.value);
    if (rgb) {
      this.pointcloud.setClassificationColor(item.code, rgb);
      item.color = [rgb[0] / 255, rgb[1] / 255, rgb[2] / 255, 1];
    }
  }

  private hexToRgb(hex: string): [number, number, number] | null {
    const res = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return res ? [parseInt(res[1], 16), parseInt(res[2], 16), parseInt(res[3], 16)] : null;
  }

  colorToHex(item: ClassificationItem): string {
    const [r, g, b] = item.color;
    const toHex = (v: number): string =>
      Math.round(v * 255)
        .toString(16)
        .padStart(2, '0');
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  }
}
